import React, { useState } from 'react';

import { Button } from '@repo/ui/Button';
import type { ButtonProps } from '@repo/ui/Button';
import gql from 'graphql-tag';
import type { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import { Toast } from '@repo/ui/Toast';
import { Mercury } from '@repo/types/Models';

export interface ReportFieldType {
  field: string;
  as: string;
}

export type ExportButtonParams = {
  children: React.ReactNode;
  filename: string;
  path: string;
  input: {
    query?: string;
    query_list?: Mercury.Queries.QueryList;
    limit?: number;
    fields: ReportFieldType[];
  };
} & Partial<Pick<ButtonProps, 'variant' | 'icon' | 'disabled'>>;

type ExportQueryResult = {
  export?: {
    data?: string;
  } | null;
};

const ExportButton = ({
  filename,
  path,
  input,
  variant,
  icon,
  disabled,
  children,
}: ExportButtonParams) => {
  const [openToast, setOpenToast] = useState<boolean>(false);

  const EXPORT_Q: TypedDocumentNode<ExportQueryResult, { input: ExportButtonParams['input'] }> =
    gql`
    query Export($input: QueryInput) {
      export(input: $input)
        @rest(path: "${path}", method: "POST", input: input, endpoint: "export") {
        data
      }
    }
  `;

  const [fetchReport, { loading }] = useLazyQuery(EXPORT_Q, {
    notifyOnNetworkStatusChange: true,
  });

  // Apollo v4 dropped onCompleted/onError on useLazyQuery, so the download has
  // to consume the promise returned by the execute function.
  const handleExport = async () => {
    try {
      const { data, error } = await fetchReport({ variables: { input: input } });
      const csv = data?.export?.data;
      if (error || !csv) {
        throw error ?? new Error('Export returned no data');
      }

      const downloadUrl = window.URL.createObjectURL(new Blob([csv]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${filename}-${new Date()}.csv`);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error(e);
      setOpenToast(true);
      setTimeout(() => {
        setOpenToast(false);
      }, 5000);
    }
  };

  return (
    <>
      <Button
        onClick={handleExport}
        loading={loading}
        variant={variant}
        icon={icon}
        disabled={disabled}
      >
        {children}
      </Button>
      <Toast
        open={openToast}
        title="Unable to export report, please try again later."
        variant="error"
        fade="left"
        position="bottom-0 right-0"
        icon="error"
        fixed
      />
    </>
  );
};

export default ExportButton;
